import { Api } from '../../api/Api';
import { CurrentPage } from '../../pages/currentPage';
import { JRpcSuccResponse } from '../../general/modules/jsonRpc/jRpcSuccResponse';
import { JsonRpcResponseFactory } from '../../general/modules/jsonRpc/jsonRpcResponseFactory';
import consoleLogger from '../../logger/consoleLogger';

const logger = consoleLogger.getPrefixedCopy('CLIENT_TYPE_SWITCH');

const SWITCH_API_ACTION = 'setClientType';
const SWITCHED_OUTCOME = 'switched';

type RequestClientTypeSwitchParams = {
    clientType: string,
    reason: string
};

type ClientTypeSwitchResult = {
    outcome: string,
    clientType?: string,
    reason?: string
};

/**
 * @description
 * Asks the site to switch the game client type for the current player.
 *
 * The page is reloaded on the `switched` outcome only. Every other outcome, every thrown error
 * and every unexpected response shape are logged and left without a reload.
 *
 * The client->js dispatchers call `handle()` without `await` and without `catch`, so a rejection
 * would leak past their `try/catch` and past Sentry. That is why everything is caught here.
 */
export class RequestClientTypeSwitch {
    public async handle(params: RequestClientTypeSwitchParams): Promise<void> {
        try {
            const response = await Api.post(SWITCH_API_ACTION, {}, {
                clientType: params.clientType,
                reason: params.reason
            });

            const jRpcResponse = new JsonRpcResponseFactory<ClientTypeSwitchResult>()
                .getJsonRpcResponseFromObject(response.data);

            if (!(jRpcResponse instanceof JRpcSuccResponse)) {
                const errorText = JSON.stringify(jRpcResponse.error);

                logger.error('client type switch failed', jRpcResponse.error);
                window.Sentry.captureException(new Error(`Client type switch error response: ${errorText}`));
                return;
            }

            if (jRpcResponse.result.outcome !== SWITCHED_OUTCOME) {
                logger.notice('client type switch was not performed', jRpcResponse.result);
                return;
            }

            logger.info('client type switched, reloading the page', jRpcResponse.result);
            CurrentPage.reload();
        } catch (error) {
            logger.error('client type switch request failed', error);
            window.Sentry.captureException(error);
        }
    }
}
